<script setup>
import { computed } from 'vue';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import BaseBubble from './Base.vue';
import { useMessageContext } from '../provider.js';

const { content, createdAt } = useMessageContext();

// Formato brasileiro: dia, mês abreviado e relógio de 24h. O `h:mm a` do
// original é o formato americano — "4:07 PM" não é como se escreve hora aqui.
const readableTime = computed(() =>
  messageTimestamp(createdAt.value, "d 'de' LLL, HH:mm")
);
const hora = computed(() => messageTimestamp(createdAt.value, 'HH:mm'));

// O ÍCONE DO EVENTO. O texto vem pronto do servidor ("John atribuiu…",
// "Conversa resolvida por…"); o desenho é só para o olho achar o tipo numa
// sequência de eventos. Enfeite: se nada casar, fica o ponto.
const TIPOS = [
  [/atribu|assign/i, 'i-lucide-user-check'],
  [/resolvid|resolved/i, 'i-lucide-circle-check'],
  [/reabert|reopen/i, 'i-lucide-rotate-ccw'],
  [/etiqueta|label|marcador/i, 'i-lucide-tag'],
  [/adiad|snooz/i, 'i-lucide-alarm-clock'],
  [/pendente|pending/i, 'i-lucide-clock'],
  [/prioridade|priority/i, 'i-lucide-flag'],
  [/equipe|time|team/i, 'i-lucide-users'],
  [/follow|retorno/i, 'i-lucide-calendar-clock'],
  [/pipeline|funil/i, 'i-lucide-kanban'],
];
const icone = computed(
  () => TIPOS.find(([padrao]) => padrao.test(content.value || ''))?.[1] || ''
);
</script>

<template>
  <!-- Evento de sistema: "John atribuiu a si mesmo", "Conversa foi reaberta".
       NÃO é mensagem — ninguém escreveu isso para ninguém. Recua: pílula de
       vidro um degrau abaixo da do dia, texto menor e o horário À VISTA (em
       auditoria de conversa, "quando" é metade da informação). -->
  <BaseBubble
    v-tooltip.top="readableTime"
    class="atv-evento"
    data-bubble-name="activity"
  >
    <i v-if="icone" class="atv-evento__ico" :class="icone" aria-hidden="true" />
    <span v-else class="atv-evento__ponto" aria-hidden="true" />
    <span v-dompurify-html="content" class="atv-evento__txt" />
    <time class="atv-evento__hora">{{ hora }}</time>
  </BaseBubble>
</template>

<style scoped>
.atv-evento {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  min-width: 0;
  max-width: min(100%, 36rem);
  padding: var(--esp-1) var(--esp-3);
  border: 1px solid rgb(var(--slate-4) / 70%);
  border-radius: var(--r-redondo);
  /* Vidro: o fundo da conversa tem desenho, e texto claro direto em cima
     dele oscilava entre legível e não conforme o traço passava por trás. */
  background: rgb(var(--solid-1) / 72%);
  backdrop-filter: blur(6px);
  transition:
    background-color var(--mov-1) var(--mov-curva),
    border-color var(--mov-1) var(--mov-curva);
}

.dark .atv-evento {
  border-color: rgb(255 255 255 / 8%);
  background: rgb(var(--surface-2) / 72%);
}

.atv-evento:hover {
  border-color: rgb(var(--slate-6));
  background: rgb(var(--solid-1));
}

.dark .atv-evento:hover {
  background: rgb(var(--surface-2));
}

.atv-evento__ico {
  flex-shrink: 0;
  font-size: var(--ico-1);
  color: rgb(var(--slate-10));
}

.atv-evento__ponto {
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-radius: var(--r-redondo);
  background: rgb(var(--slate-8));
}

.atv-evento__txt {
  min-width: 0;
  font-size: var(--txt-selo);
  line-height: var(--linha-normal);
  color: rgb(var(--slate-11));
}

/* O horário um degrau abaixo do texto: presente para auditar, discreto o
   bastante para não disputar com o que aconteceu. */
.atv-evento__hora {
  flex-shrink: 0;
  font-size: var(--txt-rotulo);
  font-variant-numeric: tabular-nums;
  color: rgb(var(--slate-10));
}
</style>
